// libs
import React, { memo, useState, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

// components
import Image from 'shared/components/ImageV2';
import ChunkFallback from 'shared/components/ChunkFallback';
import ContentWrapper from './ContentWrapper';

// styles
import { APluContainer, ViewMoreInfoCTA, ViewMoreIconContainer, ViewMoreInfoText } from './styles';

// services
import { getAPlusContent as _getAPlusContent } from 'shared/store/details/services';

// constants
import { HEADING, BANNER_ASPECT_RATION } from './constants';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';

// hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// type
import { Theme } from '@nykaa/ui-components/themes/types';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// helpers
import { log } from 'shared/helpers/newRelic';

export const VIEW_MORE_INFO = 'View More';
export const ViewMoreIconSize = '20px';

interface IAPlusContentProps {
  heroImageURL: string;
  getAPlusContent: typeof _getAPlusContent;
  productId: string;
  content: string;
  isError: boolean;
  isFetched: boolean;
  className: string;
}

function APlusContent({
  heroImageURL,
  getAPlusContent,
  productId,
  content,
  isError,
  isFetched,
  className,
}: IAPlusContentProps) {
  const [showOverlay, setShowOverlay] = useState(false);
  const containerRef = useRef(null);
  const showAPlusContent = useRemoteConfig('showAPlusContent');
  const theme: Theme = useTheme();
  const ArrowDown = useStoreIcon(IconName.CHEVRON_DOWN_SMALL);

  if (isEmpty(heroImageURL) || !showAPlusContent) {
    return null;
  }

  const openLink = async () => {
    const isEmptyAPlusContent = isEmpty(content);

    // check if data isn't already fetched
    if (isEmptyAPlusContent && isError === false && isFetched === false) {
      let data = {};

      try {
        data = await getAPlusContent(productId);
      } catch (error) {
        log({
          errTitle: 'Error in getting A Plus content - Desktop',
          error,
          tags: { [TAG_IDENTIFIER.MODULE]: 'A_PLUS_CONTENT' },
        });
      }

      if (isEmpty(data) || isEmpty(data.response.product.aplus_description)) {
        return;
      }
    }

    // if data is already fetched and is empty
    if (isEmptyAPlusContent && isError === false && isFetched === true) {
      return;
    }

    // if request has failed in the past
    if (isEmptyAPlusContent && isError === true) {
      return;
    }

    setShowOverlay(true);
  };

  const onOverlayHide = () => {
    setShowOverlay(false);
  };

  return (
    <APluContainer ref={containerRef} className={className}>
      <Image
        onClick={openLink}
        src={heroImageURL}
        width={500}
        aspectRatio={BANNER_ASPECT_RATION}
        alt={HEADING}
        className="APlusContent-banner-container"
        imgClassName="APlusContent-banner"
        data-test="APlusContent-banner-image"
      />

      <ViewMoreInfoCTA data-test="view-more" onClick={openLink}>
        <ViewMoreInfoText>{VIEW_MORE_INFO}</ViewMoreInfoText>
        <ViewMoreIconContainer>
          <ArrowDown color={theme?.colors?.secondary} size={ViewMoreIconSize} />
        </ViewMoreIconContainer>
      </ViewMoreInfoCTA>

      {showOverlay && <ContentWrapper onOverlayHide={onOverlayHide} fallback={<ChunkFallback />} />}
    </APluContainer>
  );
}

export default memo(APlusContent);
